import type { SecurityScore, RecommendationsResponse } from '../types';

interface ExecutiveSummaryProps {
  score: SecurityScore;
  recommendations?: RecommendationsResponse | null;
  alertsCount: number;
  criticalAlertsCount: number;
  tenantName?: string;
  onViewDetails?: () => void;
}

const gradeColors = {
  A: { bg: 'from-green-400 to-emerald-500', text: 'text-green-600', ring: 'ring-green-500' },
  B: { bg: 'from-blue-400 to-indigo-500', text: 'text-blue-600', ring: 'ring-blue-500' },
  C: { bg: 'from-yellow-400 to-amber-500', text: 'text-yellow-600', ring: 'ring-yellow-500' },
  D: { bg: 'from-orange-400 to-red-400', text: 'text-orange-600', ring: 'ring-orange-500' },
  F: { bg: 'from-red-500 to-rose-600', text: 'text-red-600', ring: 'ring-red-500' },
};

const statusConfig = {
  excellent: { icon: '🏆', message: 'Excelente postura de seguridad', color: 'text-green-600' },
  good: { icon: '✅', message: 'Buena postura, pocas mejoras pendientes', color: 'text-blue-600' },
  needs_attention: { icon: '⚠️', message: 'Requiere atención en algunas áreas', color: 'text-yellow-600' },
  critical: { icon: '🚨', message: 'Estado crítico - Acción inmediata requerida', color: 'text-red-600' },
};

function getStatus(score: number, criticalAlerts: number): keyof typeof statusConfig {
  if (criticalAlerts > 0) return 'critical';
  if (score >= 90) return 'excellent';
  if (score >= 75) return 'good';
  return 'needs_attention';
}

export default function ExecutiveSummary({
  score,
  recommendations,
  alertsCount,
  criticalAlertsCount,
  tenantName,
  onViewDetails
}: ExecutiveSummaryProps) {
  const status = getStatus(score.overall, criticalAlertsCount);
  const statusInfo = statusConfig[status];
  const gradeStyle = gradeColors[score.grade];

  // Calculate potential improvement
  const potentialImprovement = recommendations
    ? recommendations.potentialScore - recommendations.currentScore
    : 0;

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
      {/* Header */}
      <div className={`bg-gradient-to-r ${gradeStyle.bg} px-8 py-6`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-white text-sm font-medium opacity-90">
              {tenantName ? `Resumen Ejecutivo - ${tenantName}` : 'Resumen Ejecutivo de Seguridad'}
            </h2>
            <p className="text-white/80 text-xs mt-1">
              {new Date().toLocaleDateString('es-CL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <div className={`w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center ring-4 ${gradeStyle.ring} ring-opacity-50`}>
            <span className="text-white text-4xl font-bold">{score.grade}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-8">
        {/* Score and Status */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="text-5xl font-bold text-gray-900">{score.overall}</div>
              <div className="text-sm text-gray-500 mt-1">de 100 puntos</div>
            </div>
            <div className="h-16 w-px bg-gray-200"></div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">{statusInfo.icon}</span>
                <span className={`text-lg font-semibold ${statusInfo.color}`}>
                  {status === 'excellent' ? 'Excelente' :
                   status === 'good' ? 'Bueno' :
                   status === 'needs_attention' ? 'Atención' : 'Crítico'}
                </span>
              </div>
              <p className="text-gray-600 text-sm mt-1">{statusInfo.message}</p>
            </div>
          </div>

          {potentialImprovement > 0 && (
            <div className="bg-green-50 rounded-xl px-4 py-3 border border-green-100">
              <div className="text-xs text-green-600 font-medium">Potencial de mejora</div>
              <div className="text-2xl font-bold text-green-700">+{potentialImprovement}</div>
              <div className="text-xs text-green-600">puntos disponibles</div>
            </div>
          )}
        </div>

        {/* Three Key Metrics */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          {/* Alerts */}
          <div className={`rounded-xl p-5 ${criticalAlertsCount > 0 ? 'bg-red-50 border border-red-100' : alertsCount > 0 ? 'bg-yellow-50 border border-yellow-100' : 'bg-green-50 border border-green-100'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl">{criticalAlertsCount > 0 ? '🚨' : alertsCount > 0 ? '⚠️' : '✅'}</span>
              <span className={`text-3xl font-bold ${criticalAlertsCount > 0 ? 'text-red-700' : alertsCount > 0 ? 'text-yellow-700' : 'text-green-700'}`}>
                {alertsCount}
              </span>
            </div>
            <div className={`text-sm font-medium ${criticalAlertsCount > 0 ? 'text-red-700' : alertsCount > 0 ? 'text-yellow-700' : 'text-green-700'}`}>
              Alertas Activas
            </div>
            {criticalAlertsCount > 0 && (
              <div className="text-xs text-red-600 mt-1">
                {criticalAlertsCount} crítica{criticalAlertsCount > 1 ? 's' : ''}
              </div>
            )}
          </div>

          {/* Protection Rate */}
          <div className="rounded-xl p-5 bg-blue-50 border border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl">🛡️</span>
              <span className="text-3xl font-bold text-blue-700">
                {score.breakdown.deviceProtection}%
              </span>
            </div>
            <div className="text-sm font-medium text-blue-700">
              Dispositivos Protegidos
            </div>
            <div className="text-xs text-blue-600 mt-1">
              CrowdStrike activo
            </div>
          </div>

          {/* Quick Wins */}
          <div className="rounded-xl p-5 bg-purple-50 border border-purple-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl">⚡</span>
              <span className="text-3xl font-bold text-purple-700">
                {recommendations?.summary.quickWins || 0}
              </span>
            </div>
            <div className="text-sm font-medium text-purple-700">
              Acciones Rápidas
            </div>
            <div className="text-xs text-purple-600 mt-1">
              Mejoras inmediatas
            </div>
          </div>
        </div>

        {/* Top Recommendations */}
        {recommendations && recommendations.recommendations.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Próximas Acciones Prioritarias
            </h3>
            <div className="space-y-2">
              {recommendations.recommendations.slice(0, 3).map((rec, i) => (
                <div
                  key={rec.id}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    rec.priority === 'critical' ? 'bg-red-50 border border-red-100' :
                    rec.priority === 'high' ? 'bg-orange-50 border border-orange-100' :
                    'bg-gray-50 border border-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                      rec.priority === 'critical' ? 'bg-red-500' :
                      rec.priority === 'high' ? 'bg-orange-500' :
                      'bg-gray-500'
                    }`}>
                      {i + 1}
                    </div>
                    <span className="text-sm text-gray-800 font-medium">{rec.title}</span>
                  </div>
                  <span className="text-sm font-bold text-green-600">+{rec.impact.scoreImprovement}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* View Details Button */}
        {onViewDetails && (
          <button
            onClick={onViewDetails}
            className="w-full py-3 px-6 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition-all flex items-center justify-center gap-2"
          >
            <span>Ver Dashboard Completo</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
        )}
      </div>

      {/* Footer */}
      <div className="bg-gray-50 px-8 py-4 border-t">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Generado por Jarix Security Portal</span>
          <span>Datos en tiempo real</span>
        </div>
      </div>
    </div>
  );
}
